import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import PrivateLayout from '../components/PrivateLayout';
import PageHeader from '../components/PageHeader';
import StripeOnboarding from '../components/StripeOnboarding';
import api from '../services/api';

// ── Métadonnées des rôles ─────────────────────────────────────────────────────

const ROLES_INFO = {
  hunter: { icon: '🏹', fr: 'Chasseur', en: 'Hunter', couleur: '#2a5a3a' },
  farmer: { icon: '🌾', fr: 'Agriculteur', en: 'Farmer', couleur: '#4a6020' },
  landowner: { icon: '🏡', fr: 'Propriétaire foncier', en: 'Landowner', couleur: '#3a5848' },
  guide: { icon: '⛺', fr: 'Guide de chasse', en: 'Hunting guide', couleur: '#2a4858' },
  trapper: { icon: '🐾', fr: 'Trappeur', en: 'Trapper', couleur: '#583820' },
  arms_seller: { icon: '🔧', fr: 'Vendeur équipement', en: 'Equipment dealer', couleur: '#504020' },
  community_member: {
    icon: '🌿',
    fr: 'Membre communauté',
    en: 'Community member',
    couleur: 'var(--couleur-bordure-forte)',
  },
  admin: { icon: '⚙️', fr: 'Administrateur', en: 'Administrator', couleur: '#3a2858' },
};

// Rôles que l'utilisateur peut ajouter lui-même (admin exclu)
const ROLES_LIBRE_SERVICE = [
  'hunter',
  'landowner',
  'farmer',
  'guide',
  'trapper',
  'arms_seller',
  'community_member',
];

// ── Traductions ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Mon Profil',
    sectionIdentite: 'Identité',
    sectionRoles: 'Mes rôles actifs',
    sectionModifier: 'Modifier mes informations',
    sectionSecurite: 'Sécurité',
    labelNom: 'Nom',
    labelPrenom: 'Prénom',
    labelEmail: 'Courriel',
    labelLangue: 'Langue préférée',
    labelRole: 'Rôle principal',
    labelDepuis: 'Membre depuis',
    labelTel: 'Téléphone',
    labelCodePostal: 'Code postal',
    placeholderCP: 'ex. G1R 2K5',
    errCodePostal: 'Format invalide. Utilisez A1A 1A1 (ex. G1R 2K5).',
    langFR: 'Français',
    langEN: 'English',
    btnSauvegarder: 'Enregistrer',
    btnChgMdp: 'Changer mon mot de passe',
    btnAjouterRole: '+ Ajouter un rôle',
    btnAnnuler: 'Annuler',
    btnActiver: 'Activer ce rôle',
    titreModal: 'Ajouter un rôle à votre compte',
    descModal:
      'Chaque rôle ouvre des fonctionnalités spécifiques. Vous pouvez cumuler plusieurs rôles.',
    libreService: 'Activation immédiate',
    verification: 'Vérification requise (24-48h)',
    communauteDesc: 'Accès aux discussions, observations faune et événements communautaires.',
    nonRenseigne: '—',
    chargement: 'Chargement…',
    enregistrement: 'Enregistrement…',
    succes: 'Profil mis à jour.',
    erreurCharge: 'Impossible de charger votre profil.',
    erreurSauvegarde: 'Une erreur est survenue.',
    erreurRole: "Impossible d'ajouter ce rôle pour l'instant.",
    succesRole: 'Rôle activé avec succès.',
    statuts: {
      non_requis: '',
      en_attente: 'En attente',
      approuve: 'Approuvé',
      rejete: 'Rejeté',
    },
  },
  en: {
    title: 'My Profile',
    sectionIdentite: 'Identity',
    sectionRoles: 'My active roles',
    sectionModifier: 'Edit my information',
    sectionSecurite: 'Security',
    labelNom: 'Last name',
    labelPrenom: 'First name',
    labelEmail: 'Email',
    labelLangue: 'Preferred language',
    labelRole: 'Primary role',
    labelDepuis: 'Member since',
    labelTel: 'Phone',
    labelCodePostal: 'Postal code',
    placeholderCP: 'e.g. G1R 2K5',
    errCodePostal: 'Invalid format. Use A1A 1A1 (e.g. G1R 2K5).',
    langFR: 'Français',
    langEN: 'English',
    btnSauvegarder: 'Save',
    btnChgMdp: 'Change my password',
    btnAjouterRole: '+ Add a role',
    btnAnnuler: 'Cancel',
    btnActiver: 'Activate this role',
    titreModal: 'Add a role to your account',
    descModal: 'Each role unlocks specific features. You can have multiple roles simultaneously.',
    libreService: 'Instant activation',
    verification: 'Verification required (24-48h)',
    communauteDesc: 'Access to discussions, wildlife observations and community events.',
    nonRenseigne: '—',
    chargement: 'Loading…',
    enregistrement: 'Saving…',
    succes: 'Profile updated.',
    erreurCharge: 'Unable to load your profile.',
    erreurSauvegarde: 'An error occurred.',
    erreurRole: 'Unable to add this role right now.',
    succesRole: 'Role activated successfully.',
    statuts: {
      non_requis: '',
      en_attente: 'Pending',
      approuve: 'Approved',
      rejete: 'Rejected',
    },
  },
};

// ── Utilitaires ───────────────────────────────────────────────────────────────

function formaterDate(dateStr, lang) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(lang === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

function rolesVerification(role) {
  return ['farmer', 'guide', 'arms_seller', 'trapper'].includes(role);
}

// ── Composant ─────────────────────────────────────────────────────────────────

export default function MonProfil() {
  const { lang } = useContext(LangContext);
  const t = T[lang];
  const { utilisateur: authUser, connecter } = useAuth();
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);
  const [form, setForm] = useState({
    name: '',
    first_name: '',
    phone: '',
    postal_code: '',
    language: lang,
  });
  const [chargementPage, setChargementPage] = useState(true);
  const [chargementForm, setChargementForm] = useState(false);
  const [succesForm, setSuccesForm] = useState('');
  const [erreurForm, setErreurForm] = useState('');
  const [erreurPage, setErreurPage] = useState('');
  const [afficherModal, setAfficherModal] = useState(false);
  const [roleEnCours, setRoleEnCours] = useState(null);
  const [msgRole, setMsgRole] = useState({ type: '', texte: '' });

  // ── Chargement du profil via /auth/me ──────────────────────────────────────
  const chargerProfil = useCallback(async () => {
    setChargementPage(true);
    setErreurPage('');
    try {
      const rep = await api.get('/auth/me');
      const u = rep.data;
      setUserData(u);
      setForm({
        name: u.name || '',
        first_name: u.first_name || '',
        phone: u.phone || '',
        postal_code: u.postal_code || '',
        language: u.language || lang,
      });
    } catch {
      setErreurPage(t.erreurCharge);
      // Repli sur les données du contexte auth
      if (authUser) {
        setUserData(authUser);
        setForm({
          name: authUser.name || '',
          first_name: authUser.first_name || '',
          phone: authUser.phone || '',
          postal_code: authUser.postal_code || '',
          language: authUser.language || lang,
        });
      }
    } finally {
      setChargementPage(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    chargerProfil();
  }, [chargerProfil]);

  // ── Soumission du formulaire ───────────────────────────────────────────────
  const normaliserCodePostal = (val) => {
    const brut = val.toUpperCase().replace(/\s/g, '');
    if (brut.length === 6) return `${brut.slice(0, 3)} ${brut.slice(3)}`;
    return val.toUpperCase();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.postal_code) {
      const cp = normaliserCodePostal(form.postal_code);
      if (!/^[A-Z]\d[A-Z] \d[A-Z]\d$/.test(cp)) {
        setErreurForm(t.errCodePostal);
        return;
      }
    }
    setChargementForm(true);
    setSuccesForm('');
    setErreurForm('');
    try {
      const payload = {
        ...form,
        postal_code: form.postal_code ? normaliserCodePostal(form.postal_code) : '',
      };
      const rep = await api.put('/me/profile', payload);
      const userMaj = rep.data?.user || { ...userData, ...form };
      setUserData(userMaj);
      connecter(localStorage.getItem('token'), userMaj);
      setSuccesForm(t.succes);
    } catch (err) {
      setErreurForm(err.response?.data?.message || t.erreurSauvegarde);
    } finally {
      setChargementForm(false);
    }
  };

  // ── Ajout d'un rôle ───────────────────────────────────────────────────────
  const handleAjouterRole = async (role) => {
    setRoleEnCours(role);
    setMsgRole({ type: '', texte: '' });
    try {
      await api.post('/auth/roles/ajouter', { role });
      setMsgRole({ type: 'succes', texte: t.succesRole });
      await chargerProfil();
      setTimeout(() => setAfficherModal(false), 1200);
    } catch (err) {
      setMsgRole({
        type: 'erreur',
        texte: err.response?.data?.message || t.erreurRole,
      });
    } finally {
      setRoleEnCours(null);
    }
  };

  // ── Données dérivées ──────────────────────────────────────────────────────
  const rolesActifs = userData?.roles?.filter((r) => r.active !== false) || [];
  const rolesActifsIds = new Set(rolesActifs.map((r) => r.role));
  const rolesDisponibles = ROLES_LIBRE_SERVICE.filter((r) => !rolesActifsIds.has(r));

  const initiales =
    [userData?.first_name, userData?.name]
      .filter(Boolean)
      .map((s) => s[0].toUpperCase())
      .join('') ||
    userData?.name?.[0]?.toUpperCase() ||
    '?';

  // ── Styles ────────────────────────────────────────────────────────────────

  const s = {
    page: {
      minHeight: '100vh',
      background: 'var(--couleur-fond)',
      color: 'var(--couleur-texte)',
    },
    main: {
      maxWidth: '860px',
      margin: '0 auto',
      paddingBottom: '4rem',
    },
    h1: {
      fontFamily: 'Georgia, serif',
      fontSize: '1.85rem',
      fontWeight: 700,
      color: 'var(--couleur-or)',
      marginBottom: '2rem',
      paddingBottom: '0.6rem',
      borderBottom: '1px solid rgba(184,134,11,0.22)',
    },
    carte: {
      background: 'var(--couleur-fond-carte)',
      border: '1px solid var(--couleur-bordure-forte)',
      borderRadius: '10px',
      padding: '1.6rem 1.8rem',
      marginBottom: '1.4rem',
    },
    titreCarte: {
      fontFamily: 'Georgia, serif',
      fontSize: '1rem',
      fontWeight: 700,
      color: 'var(--couleur-or)',
      marginBottom: '1.2rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.4rem',
    },
    label: {
      display: 'block',
      fontSize: '0.79rem',
      color: 'rgba(232,224,208,0.55)',
      marginBottom: '0.25rem',
      fontWeight: 500,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
    },
    value: {
      fontSize: '0.95rem',
      color: 'var(--couleur-texte)',
      padding: '0.3rem 0',
    },
    input: {
      background: 'var(--couleur-fond-carte)',
      border: '1px solid var(--couleur-bordure-forte)',
      borderRadius: '6px',
      color: 'var(--couleur-texte)',
      fontSize: '0.95rem',
      padding: '0.55rem 0.8rem',
      width: '100%',
      outline: 'none',
      transition: 'border-color 0.2s',
    },
    select: {
      background: 'var(--couleur-fond-carte)',
      border: '1px solid var(--couleur-bordure-forte)',
      borderRadius: '6px',
      color: 'var(--couleur-texte)',
      fontSize: '0.95rem',
      padding: '0.55rem 0.8rem',
      width: '100%',
      outline: 'none',
      transition: 'border-color 0.2s',
      cursor: 'pointer',
    },
    btnOr: {
      background: 'var(--couleur-or)',
      border: 'none',
      borderRadius: '6px',
      color: 'var(--couleur-fond-carte)',
      cursor: 'pointer',
      fontSize: '0.93rem',
      fontWeight: 700,
      padding: '0.6rem 1.5rem',
      transition: 'filter 0.15s',
    },
    btnContour: {
      background: 'transparent',
      border: '1px solid var(--couleur-or)',
      borderRadius: '6px',
      color: 'var(--couleur-or)',
      cursor: 'pointer',
      fontSize: '0.88rem',
      fontWeight: 600,
      padding: '0.45rem 1.1rem',
      transition: 'background 0.15s',
    },
    grille2: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '1rem',
    },
    msgSucces: {
      background: 'rgba(26,74,26,0.4)',
      border: '1px solid rgba(26,200,26,0.25)',
      borderRadius: '6px',
      color: 'var(--couleur-texte-succes)',
      padding: '0.65rem 1rem',
      marginBottom: '1rem',
      fontSize: '0.9rem',
    },
    msgErreur: {
      background: 'rgba(120,20,20,0.35)',
      border: '1px solid rgba(200,40,40,0.35)',
      borderRadius: '6px',
      color: 'var(--couleur-texte-erreur)',
      padding: '0.65rem 1rem',
      marginBottom: '1rem',
      fontSize: '0.9rem',
    },
  };

  // ── Spinner de chargement ─────────────────────────────────────────────────
  if (chargementPage && !userData) {
    return (
      <PrivateLayout>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '60vh',
          }}
        >
          <span
            style={{ color: 'rgba(255,255,255,0.2)', fontSize: '1.5rem', letterSpacing: '0.2em' }}
          >
            ···
          </span>
        </div>
      </PrivateLayout>
    );
  }

  // ── Rendu principal ───────────────────────────────────────────────────────
  return (
    <PrivateLayout>
      <main style={s.main}>
        <PageHeader icon="👤" title={t.title} />

        {erreurPage && <div style={s.msgErreur}>{erreurPage}</div>}

        {/* ── Carte identité ─────────────────────────────────────────────── */}
        <div style={s.carte}>
          <div style={s.titreCarte}>
            <span>👤</span>
            {t.sectionIdentite}
          </div>

          <div
            style={{ display: 'flex', gap: '1.8rem', alignItems: 'flex-start', flexWrap: 'wrap' }}
          >
            {/* Avatar initiales */}
            <div
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'var(--couleur-bordure-forte)',
                border: '2px solid var(--couleur-or)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.55rem',
                  fontWeight: 700,
                  color: 'var(--couleur-or)',
                }}
              >
                {initiales}
              </span>
            </div>

            {/* Infos lecture seule */}
            <div
              style={{
                flex: 1,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '0.7rem 2rem',
                minWidth: '240px',
              }}
            >
              <div>
                <span style={s.label}>{t.labelNom}</span>
                <div style={s.value}>{userData?.name || t.nonRenseigne}</div>
              </div>
              <div>
                <span style={s.label}>{t.labelEmail}</span>
                <div style={{ ...s.value, color: 'var(--couleur-or)', wordBreak: 'break-word' }}>
                  {userData?.email || t.nonRenseigne}
                </div>
              </div>
              <div>
                <span style={s.label}>{t.labelDepuis}</span>
                <div style={s.value}>{formaterDate(userData?.registered_at, lang)}</div>
              </div>
              <div>
                <span style={s.label}>{t.labelRole}</span>
                <div style={s.value}>
                  {userData?.user_type ? (
                    <span
                      style={{
                        display: 'inline-block',
                        background: 'rgba(184,134,11,0.13)',
                        border: '1px solid rgba(184,134,11,0.3)',
                        borderRadius: '4px',
                        color: 'var(--couleur-or)',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        padding: '0.15rem 0.55rem',
                      }}
                    >
                      {ROLES_INFO[userData.user_type]?.[lang] || userData.user_type}
                    </span>
                  ) : (
                    t.nonRenseigne
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Carte rôles actifs ─────────────────────────────────────────── */}
        <div style={s.carte}>
          <div
            style={{
              ...s.titreCarte,
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '0.5rem',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span>🎭</span>
              {t.sectionRoles}
            </span>
            {rolesDisponibles.length > 0 && (
              <button
                style={s.btnContour}
                onClick={() => {
                  setAfficherModal((v) => !v);
                  setMsgRole({ type: '', texte: '' });
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(184,134,11,0.1)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {afficherModal ? t.btnAnnuler : t.btnAjouterRole}
              </button>
            )}
          </div>

          {/* Badges des rôles actifs */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '0.6rem',
              marginBottom: rolesActifs.length ? '0.4rem' : 0,
            }}
          >
            {rolesActifs.length === 0 && (
              <span
                style={{ color: 'rgba(232,224,208,0.4)', fontSize: '0.88rem', fontStyle: 'italic' }}
              >
                {t.nonRenseigne}
              </span>
            )}
            {rolesActifs.map((r) => {
              const info = ROLES_INFO[r.role] || {};
              const status = t.statuts[r.status];
              return (
                <div
                  key={r.role}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    background: info.couleur || 'var(--couleur-bordure-forte)',
                    border: '1px solid rgba(184,134,11,0.2)',
                    borderRadius: '20px',
                    padding: '0.3rem 0.85rem',
                    fontSize: '0.85rem',
                    color: 'var(--couleur-texte)',
                    fontWeight: 500,
                  }}
                >
                  <span>{info.icon || '●'}</span>
                  <span>{info[lang] || r.role}</span>
                  {status && (
                    <span
                      style={{
                        fontSize: '0.7rem',
                        background:
                          r.status === 'approuve'
                            ? 'rgba(50,160,50,0.25)'
                            : 'rgba(200,160,30,0.25)',
                        color:
                          r.status === 'approuve'
                            ? 'var(--couleur-texte-succes)'
                            : 'var(--couleur-texte-warning)',
                        borderRadius: '10px',
                        padding: '0.08rem 0.4rem',
                        marginLeft: '0.1rem',
                      }}
                    >
                      {status}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Panneau d'ajout de rôle ─────────────────────────────────── */}
          {afficherModal && (
            <div
              style={{
                marginTop: '1.2rem',
                background: 'rgba(28,58,28,0.35)',
                border: '1px solid var(--couleur-bordure-forte)',
                borderRadius: '8px',
                padding: '1.2rem',
              }}
            >
              <p
                style={{
                  fontSize: '0.88rem',
                  color: 'rgba(232,224,208,0.65)',
                  marginBottom: '1rem',
                  lineHeight: 1.5,
                }}
              >
                {t.descModal}
              </p>

              {msgRole.texte && (
                <div style={msgRole.type === 'succes' ? s.msgSucces : s.msgErreur}>
                  {msgRole.texte}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                {rolesDisponibles.map((role) => {
                  const info = ROLES_INFO[role] || {};
                  const avecVerif = rolesVerification(role);
                  const enCours = roleEnCours === role;
                  return (
                    <div
                      key={role}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'var(--couleur-fond-carte)',
                        border: '1px solid var(--couleur-bordure-forte)',
                        borderRadius: '7px',
                        padding: '0.7rem 1rem',
                        gap: '1rem',
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <span style={{ fontSize: '1.1rem' }}>{info.icon || '●'}</span>
                        <div>
                          <div
                            style={{
                              fontSize: '0.92rem',
                              fontWeight: 600,
                              color: 'var(--couleur-texte)',
                            }}
                          >
                            {info[lang] || role}
                          </div>
                          <div
                            style={{
                              fontSize: '0.75rem',
                              color: 'rgba(232,224,208,0.45)',
                              marginTop: '1px',
                            }}
                          >
                            {avecVerif ? `⏳ ${t.verification}` : `✓ ${t.libreService}`}
                            {role === 'community_member' && (
                              <span style={{ display: 'block', marginTop: '2px' }}>
                                {t.communauteDesc}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        disabled={enCours}
                        onClick={() => handleAjouterRole(role)}
                        style={{
                          ...s.btnOr,
                          fontSize: '0.82rem',
                          padding: '0.4rem 1rem',
                          opacity: enCours ? 0.6 : 1,
                          cursor: enCours ? 'not-allowed' : 'pointer',
                          flexShrink: 0,
                        }}
                        onMouseEnter={(e) => {
                          if (!enCours) e.currentTarget.style.filter = 'brightness(1.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.filter = 'brightness(1)';
                        }}
                      >
                        {enCours ? '···' : t.btnActiver}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* ── Carte modifier infos ───────────────────────────────────────── */}
        <div style={s.carte}>
          <div style={s.titreCarte}>
            <span>✏️</span>
            {t.sectionModifier}
          </div>

          {succesForm && <div style={s.msgSucces}>{succesForm}</div>}
          {erreurForm && <div style={s.msgErreur}>{erreurForm}</div>}

          <form onSubmit={handleSubmit} noValidate>
            <div style={{ ...s.grille2, marginBottom: '1rem' }}>
              <div>
                <label htmlFor="mp-name" style={s.label}>
                  {t.labelNom}
                </label>
                <input
                  id="mp-name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  style={s.input}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')
                  }
                  autoComplete="family-name"
                />
              </div>
              <div>
                <label htmlFor="mp-first_name" style={s.label}>
                  {t.labelPrenom}
                </label>
                <input
                  id="mp-first_name"
                  type="text"
                  value={form.first_name}
                  onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
                  style={s.input}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')
                  }
                  autoComplete="given-name"
                />
              </div>
            </div>

            <div style={{ ...s.grille2, marginBottom: '1rem' }}>
              <div>
                <label htmlFor="mp-tel" style={s.label}>
                  {t.labelTel}
                </label>
                <input
                  id="mp-tel"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  style={s.input}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                  onBlur={(e) =>
                    (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')
                  }
                  autoComplete="tel"
                  placeholder="418-555-0100"
                />
              </div>
              <div>
                <label htmlFor="mp-cp" style={s.label}>
                  {t.labelCodePostal}
                </label>
                <input
                  id="mp-cp"
                  type="text"
                  value={form.postal_code}
                  onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)';
                    if (form.postal_code) {
                      setForm((f) => ({ ...f, postal_code: normaliserCodePostal(f.postal_code) }));
                    }
                  }}
                  style={s.input}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                  autoComplete="postal-code"
                  placeholder={t.placeholderCP}
                  maxLength={7}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1.2rem' }}>
              <label htmlFor="mp-language" style={s.label}>
                {t.labelLangue}
              </label>
              <select
                id="mp-language"
                value={form.language}
                onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
                style={{ ...s.select, maxWidth: '220px' }}
                onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-or)')}
                onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--couleur-bordure-forte)')}
              >
                <option value="fr">{t.langFR}</option>
                <option value="en">{t.langEN}</option>
              </select>
            </div>

            <button
              type="submit"
              disabled={chargementForm}
              style={{
                ...s.btnOr,
                opacity: chargementForm ? 0.7 : 1,
                cursor: chargementForm ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!chargementForm) e.currentTarget.style.filter = 'brightness(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = 'brightness(1)';
              }}
            >
              {chargementForm ? t.enregistrement : t.btnSauvegarder}
            </button>
          </form>
        </div>

        {/* ── Carte Stripe (proprietaire / agriculteur) ─────────────────── */}
        {(() => {
          const estVendeur =
            rolesActifs.some((r) => ['landowner', 'farmer'].includes(r.role)) ||
            ['landowner', 'farmer'].includes(userData?.user_type);
          if (!estVendeur) return null;
          // StripeOnboarding ships its own card chrome + title — render it
          // directly so we don't double up the border / padding / heading.
          return (
            <div style={{ marginBottom: '1.4rem' }}>
              <StripeOnboarding />
            </div>
          );
        })()}

        {/* ── Carte sécurité ─────────────────────────────────────────────── */}
        <div style={s.carte}>
          <div style={s.titreCarte}>
            <span>🔒</span>
            {t.sectionSecurite}
          </div>
          <button
            style={s.btnContour}
            onClick={() => navigate('/security')}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(184,134,11,0.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            {t.btnChgMdp}
          </button>
        </div>
      </main>
    </PrivateLayout>
  );
}
